import { getRenderedAttributes, mergeAttributes } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

import { createResizableNodeView } from "./resizable";

const DEFAULT_MIN_WIDTH = 50;
const DEFAULT_MIN_HEIGHT = 50;

/**
 * TipTap Image's built-in resize uses `setNodeSelection` on commit and does not
 * clean up `touchmove` / listen for `touchend`. That leaves sticky resizes in
 * the mobile webview. Reuse the same ResizableNodeView path as video/iframe.
 */
export const ResizableImage = Image.extend({
  addNodeView() {
    if (
      !this.options.resize ||
      !this.options.resize.enabled ||
      typeof document === "undefined"
    ) {
      return null;
    }

    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } =
      this.options.resize;
    const resizeManagedAttributes = new Set(["src", "width", "height"]);
    const nodeName = this.name;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const el = document.createElement("img");
      el.draggable = false;

      const mergedAttributes = mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
      );

      Object.entries(mergedAttributes).forEach(([key, value]) => {
        if (value == null) {
          return;
        }
        switch (key) {
          case "src":
          case "width":
          case "height":
            break;
          default:
            el.setAttribute(key, String(value));
            break;
        }
      });

      if (mergedAttributes.src != null) {
        el.src = String(mergedAttributes.src);
      }

      let previousHTMLAttributes = { ...HTMLAttributes };
      const syncImageSource = (src: unknown) => {
        if (typeof src === "string" && src !== "") {
          if (el.getAttribute("src") !== src) {
            el.src = src;
          }
          return;
        }
        if (el.hasAttribute("src")) {
          el.removeAttribute("src");
        }
        if (el.src !== "") {
          el.src = "";
        }
      };

      syncImageSource(HTMLAttributes.src);

      if (node.attrs.width) {
        el.style.width = `${node.attrs.width}px`;
      }
      if (node.attrs.height) {
        el.style.height = `${node.attrs.height}px`;
      }

      const onUpdate = (updatedNode: ProseMirrorNode) => {
        if (updatedNode.type.name !== nodeName) {
          return false;
        }

        const extensionAttributes = editor.extensionManager.attributes.filter(
          (attribute) => attribute.type === updatedNode.type.name,
        );
        const newHTMLAttributes = getRenderedAttributes(
          updatedNode,
          extensionAttributes,
        );

        Object.keys(previousHTMLAttributes).forEach((key) => {
          if (
            !resizeManagedAttributes.has(key) &&
            !(key in newHTMLAttributes)
          ) {
            el.removeAttribute(key);
          }
        });

        Object.entries(newHTMLAttributes).forEach(([key, value]) => {
          if (resizeManagedAttributes.has(key)) {
            return;
          }
          if (value != null) {
            el.setAttribute(key, String(value));
          } else {
            el.removeAttribute(key);
          }
        });

        syncImageSource(newHTMLAttributes.src);
        previousHTMLAttributes = newHTMLAttributes;

        if (updatedNode.attrs.width) {
          el.style.width = `${updatedNode.attrs.width}px`;
        }
        if (updatedNode.attrs.height) {
          el.style.height = `${updatedNode.attrs.height}px`;
        }

        return true;
      };

      const nodeView = createResizableNodeView({
        element: el,
        node,
        editor,
        getPos,
        nodeName,
        minWidth: minWidth ?? DEFAULT_MIN_WIDTH,
        minHeight: minHeight ?? DEFAULT_MIN_HEIGHT,
        preserveAspectRatio: alwaysPreserveAspectRatio === true,
        directions: directions?.length ? [...directions] : undefined,
        onUpdate,
      });

      const dom = nodeView.dom as HTMLElement;
      // Hide until load so TipTap can measure intrinsic size first.
      dom.style.visibility = "hidden";
      dom.style.pointerEvents = "none";
      el.onload = () => {
        dom.style.visibility = "";
        dom.style.pointerEvents = "";
      };
      if (el.complete && el.naturalWidth > 0) {
        dom.style.visibility = "";
        dom.style.pointerEvents = "";
      }

      return nodeView;
    };
  },
});
